import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaBars } from "react-icons/fa";
import CategoryList from "./CategoryList";
import ProductCard from "../components/ProductCard";
import HeroBanner from "../components/HeroBanner";
import "../styles/home.css";
import Spinner from "../components/Spinner";

export default function Home() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [cursor, setCursor] = useState(null);
  const [hasNext, setHasNext] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const observer = useRef(null);

  // Reset when category changes
  useEffect(() => {
    setSelectedCategory(categoryId || "");
    setProducts([]);
    setCursor(null);
    setHasNext(true);
  }, [categoryId]);

  // Load categories
  useEffect(() => {
    axios
      .get(`${PRODUCT_API}/products/categories/`)
      .then((res) => setCategories(res.data?.content || []))
      .catch(console.error);
  }, [PRODUCT_API]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    if (loading || !hasNext) return;
    try {
      setLoading(true);
      setError("");
      const { data } = await axios.get(
        `${PRODUCT_API}/products/public/buyers`,
        {
          params: {
            cursor,
            limit: 20,
            categoryId: selectedCategory || undefined,
          },
        },
      );
      setProducts((prev) => [...prev, ...(data.content || [])]);
      setCursor(data.nextCursor);
      setHasNext(data.hasNext);
    } catch {
      setError("Failed to load products");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [cursor, hasNext, loading, selectedCategory, PRODUCT_API]);

  // Initial load
  useEffect(() => {
    if (products.length === 0) fetchProducts();
  }, [selectedCategory]);

  // Infinite scroll
  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNext) fetchProducts();
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasNext, fetchProducts],
  );

  return (
    <>
      <div className="home-page">
        <HeroBanner />
        {/* Category Button */}
        <div className="d-flex align-items-center border-bottom p-3 bg-white shadow-sm">
          <button
            className="btn category-menu-btn p-0 me-3"
            data-bs-toggle="offcanvas"
            data-bs-target="#categorySidebar"
          >
            <FaBars size={22} />
          </button>
          <h6 className="fw-bold mb-0">Categories</h6>
        </div>

        {/* Category Sidebar */}
        <div
          className="offcanvas offcanvas-start"
          tabIndex="-1"
          id="categorySidebar"
        >
          <div className="offcanvas-header border-bottom">
            <h6 className="fw-bold">Categories</h6>
            <button className="btn-close" data-bs-dismiss="offcanvas" />
          </div>
          <div className="offcanvas-body">
            <CategoryList
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={(id) => navigate(`/category/${id}`)}
              itemProps={{ "data-bs-dismiss": "offcanvas" }}
            />
          </div>
        </div>

        {/* Products */}
        <div className="container-fluid mt-4">
          <div className="row g-3">
            {initialLoading ? (
              <Spinner />
            ) : (
              products.map((product, index) => {
                const last = index === products.length - 1;
                return (
                  <div
                    key={product.id}
                    ref={last ? lastProductRef : null}
                    className="col-6 col-sm-4 col-md-3"
                  >
                    <ProductCard product={product} />
                  </div>
                );
              })
            )}
          </div>

          {loading && !initialLoading && (
            <div className="text-center my-4">
              <div className="spinner-border" />
              <p>Loading more products...</p>
            </div>
          )}

          {!hasNext && products.length > 0 && (
            <div className="text-center text-muted my-4">No more products</div>
          )}

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </div>
    </>
  );
}
